/* eslint-disable react-hooks/refs */
// this will be removed during the migration to TS for that component
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { deleteMedia, uploadMedia, fetchRetryMedia, uploadMediaSecureUrl } from 'actions/mediaActions';
import { getContentType } from 'components/common/UploadModal/utils/getContentType';
import { restClient } from 'config/store/configureStore';

import { MEDIAS_API_UPLOAD } from 'constants/ApiConstants';
import {
  MEDIA_TYPE,
  STATUS_FAILED,
  STATUS_LOADED,
  STATUS_LOADING,
  STATUS_DELETED,
  ALLOWED_MEDIA_TYPES_FOR_THUMBNAIL,
} from 'constants/MediaConstants';

import useMediaRules from 'hooks/useMediaRules';
import { Button, Dragger, Upload } from 'ui/base';
import useNotification from 'ui/base/Notification';
import { getExtension } from 'utils/fileName';

import * as styles from './MediasUploader.module.less';
import { allowedFileValidator, filterDeletedMedia, getError } from './utils';

/** Return all medias (uploaded and in queue), remove ghost entities from "queue" */
const getAllMedias = (medias = [], waiting = []) => {
  const currentUUIDs = medias.map(({ uuid }) => uuid);
  return filterDeletedMedia([...waiting.filter(({ uuid }) => !currentUUIDs.includes(uuid)), ...medias]);
};

/** @type {React.FC<import('./types').MediaUploaderProps>} */
const MediaUploader = React.forwardRef(
  (
    {
      limit,
      render,
      allowed,
      readOnly = false,
      ignoreId = false,
      validator,
      onlyRender = false,
      isDisabled = false,
      uploadURL, // the url where to get the url, and where to fetch retry to upload ex: `audit/api/1234/questions/1234/medias, the url?contentType=... is added on the code below.
      mediaMetadata,
      isTwoStepUpload = false,
      medias: values = [],
      displayMaxSizeMessage = false,
      withButtonToUpload = false,
      withDraggerToUpload = false,
      uploadButtonProps = {},
      deleteCallback = () => {},
      onUploaded = () => undefined,
      params: urlToUploadParams = {},
      uploadLabel = 'action.upload_media',
      delegateToForm = false, // In this case, the thumbnails is not fetched and the data persistance should be manage by a parent form
    },
    reference
  ) => {
    const intl = useIntl();
    const notification = useNotification();

    const configuration = useMediaRules(allowed);

    // @todo - Remove this workaround, the parents should give undefined or an Array
    const mediaList = values || [];

    const [queue, setQueue] = React.useState(/** @type {import('./types').MediaUploaderItem} */ []);
    const currentMedias = React.useRef(/** @type {import('./types').MediaUploaderItem} */ []);

    const canUpload = !readOnly && (!limit || (queue.length < limit && currentMedias.current.length < limit));
    const showUploadButton = canUpload && withButtonToUpload;
    const showUploadDragger = canUpload && withDraggerToUpload;

    const draggerProps = { multiple: !limit || limit > 1, maxCount: limit };

    React.useLayoutEffect(() => {
      if (Array.isArray(mediaList)) {
        const tempCurrentMedia = mediaList.map(media => {
          // If we've already the media, we merge the both entries to keep all information
          const existingMedia = currentMedias.current.find(item => item.data?.id === media.id);

          const defaultMedia = {
            uuid: uuidv4(),
            file: {},
            data: media,
            status: STATUS_LOADED,
          };

          return {
            ...defaultMedia,
            ...(existingMedia ?? {}),
            uuid: existingMedia?.data?.mediaLibraryItemId ? uuidv4() : existingMedia?.uuid ?? defaultMedia.uuid,
            data: media,
          };
        });
        const currentUUIDs = tempCurrentMedia.map(({ uuid }) => uuid);
        currentMedias.current = tempCurrentMedia;
        setQueue(queue.filter(({ uuid }) => !currentUUIDs.includes(uuid)));
      }
    }, [values]);

    const updateStateMedia = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      storedMedia => {
        const index = currentMedias.current.findIndex(({ uuid }) => uuid === storedMedia.uuid);

        const newState = [...currentMedias.current];
        if (index === -1) {
          newState.push(storedMedia);
        } else {
          newState.splice(index, 1, storedMedia);
        }

        onUploaded(newState);
        currentMedias.current = newState;

        const isSuccess = storedMedia.status === STATUS_LOADED;
        if (isSuccess) {
          setQueue(item => item.filter(({ uuid }) => uuid !== storedMedia.uuid));
        }
      },
      [onUploaded]
    );

    const deleteStateMedia = React.useCallback(
      /**
       * @param {import('./types').MediaUploaderItem} storedMedia
       * @param {boolean} dispatchAction
       */
      (storedMedia, dispatchAction = false) => {
        let newState;
        const mediaId = storedMedia?.data?.id;

        // If we delegate to the form, we ned to update the status to DELETED
        // If the media was just uploaded, we don't need to do it, it can be removed from the array
        if (delegateToForm && !storedMedia.isUploaded && !storedMedia.data.isUploaded) {
          newState = currentMedias.current.map(media => {
            const status = media.data.id === mediaId ? STATUS_DELETED : media.data.status;
            return {
              ...media,
              data: {
                ...media.data,
                status,
              },
            };
          });
        } else {
          newState = currentMedias.current.filter(({ data }) => data.id !== mediaId);
        }

        onUploaded(newState);
        currentMedias.current = newState;

        if (dispatchAction) {
          deleteCallback(mediaId);
        }
      },
      [deleteCallback, onUploaded, delegateToForm]
    );

    const tryToFetch = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      async storedMedia => {
        let media;
        try {
          media = await fetchRetryMedia(uploadURL, ignoreId ? undefined : storedMedia.uploadedId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Try to fetch error', error);
        }

        updateStateMedia({
          ...storedMedia,
          data: media ?? {},
          status: media ? STATUS_LOADED : STATUS_FAILED,
        });
      },
      [ignoreId, updateStateMedia, uploadURL]
    );

    const createMediaFromFile = (file, data) => {
      const thumbnailPath = ALLOWED_MEDIA_TYPES_FOR_THUMBNAIL.includes(data.type)
        ? URL.createObjectURL(file)
        : undefined;

      const blobUrl = data.type === MEDIA_TYPE.DOCUMENT ? URL.createObjectURL(file) : undefined;

      return {
        ...data,
        thumbnailPath,
        path: thumbnailPath ?? blobUrl,
        originalPath: data.path ?? blobUrl,
        id: data.id ?? data.uploadId,
      };
    };

    const uploadWithGetUrl = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      async storedMedia => {
        const { file } = storedMedia;
        const params = {
          ...urlToUploadParams,
          contentType: getContentType(storedMedia),
          filename: file.name,
          order: storedMedia.order,
        };

        let media;
        let mediaId;
        try {
          /** @type {import('axios').AxiosResponse<import('modules/medias/types/abstract-media').AbstractMedia>} */
          const { data } = await restClient.get(`${uploadURL}/url`, { params });
          mediaId = data.id;
          await uploadMediaSecureUrl(data.url, data.fields, file);
          if (delegateToForm) {
            media = createMediaFromFile(file, data);
          } else {
            media = await fetchRetryMedia(uploadURL, ignoreId ? undefined : mediaId);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Upload without get url error', error);
        }

        updateStateMedia({
          ...storedMedia,
          data: media ?? {},
          status: media ? STATUS_LOADED : STATUS_FAILED,
          isUploaded: !!media,
          uploadedId: mediaId,
        });
      },
      [ignoreId, updateStateMedia, uploadURL, urlToUploadParams, delegateToForm]
    );

    const uploadWithoutGetUrl = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      async storedMedia => {
        let data;

        try {
          const response = await uploadMedia(
            `${uploadURL}?extension=${getExtension(storedMedia.file.name)}`,
            storedMedia.file
          );

          data = await fetchRetryMedia(uploadURL, ignoreId ? undefined : response.data.id);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Upload without get url error', error);
        }

        updateStateMedia({
          ...storedMedia,
          data: data ?? {},
          status: data ? STATUS_LOADED : STATUS_FAILED,
        });
      },
      [ignoreId, updateStateMedia, uploadURL]
    );

    const onUpload = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      storedMedia => {
        if (isTwoStepUpload) {
          uploadWithGetUrl(storedMedia);
        } else {
          uploadWithoutGetUrl(storedMedia);
        }
      },
      [isTwoStepUpload, uploadWithGetUrl, uploadWithoutGetUrl]
    );

    const limitValidator = React.useCallback(() => {
      if (limit && queue.length >= limit) {
        return intl.formatMessage({
          id: 'page.fiche.medias.error.limit',
          defaultMessage: 'Media limit has been reached.',
        });
      }
      return undefined;
    }, [intl, limit, queue]);

    const onBeforeUpload = React.useCallback(
      /** @param {File} file */
      async file => {
        const error = await getError(file, [
          fileToValidate => allowedFileValidator(fileToValidate, intl, configuration.extensions, configuration.fileTypes),
          limitValidator,
          validator,
        ]);
        if (!error) {
          const media = {
            uuid: uuidv4(),
            file,
            data: {},
            status: STATUS_LOADING,
            metadata: mediaMetadata,
          };

          setQueue(oldArray => {
            const order = Math.max(oldArray.map(m => m.order ?? oldArray.length + 1));
            onUpload({ ...media, order });
            return [...oldArray, { ...media, order }];
          });
        } else {
          notification.error({ title: error });
        }

        // return a rejected Promise to stop the uploader.
        return Promise.reject();
      },
      [limitValidator, validator, onUpload, intl, configuration, mediaMetadata, notification]
    );

    const onRetry = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      async storedMedia => {
        updateStateMedia({ ...storedMedia, status: STATUS_LOADING });
        setQueue(items =>
          items.map(item => (item.uuid === storedMedia.uuid ? { ...item, status: STATUS_LOADING } : item)));

        const uploadFn = storedMedia.isUploaded ? tryToFetch : onUpload;
        return uploadFn(storedMedia);
      },
      [onUpload, tryToFetch, updateStateMedia]
    );

    const onUpdate = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      async storedMedia => {
        updateStateMedia({ ...storedMedia, status: STATUS_LOADING });

        let status = STATUS_LOADED;
        try {
          if (urlToUploadParams.entity) {
            await restClient.put(`${MEDIAS_API_UPLOAD}/${storedMedia.data.id}`, storedMedia.data);
          } else {
            await restClient.patch(`${uploadURL}/${storedMedia.data.id}`, storedMedia.data);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('On update error', error);
          status = STATUS_FAILED;
        }
        updateStateMedia({ ...storedMedia, status });
      },
      [updateStateMedia, uploadURL, urlToUploadParams]
    );

    const onDelete = React.useCallback(
      /** @param {import('./types').MediaUploaderItem} storedMedia */
      async storedMedia => {
        if (storedMedia.data?.isDuplicated) {
          deleteStateMedia(storedMedia, true);
        } else if (storedMedia.status === STATUS_FAILED) {
          deleteStateMedia(storedMedia);
        } else if (delegateToForm) {
          deleteStateMedia(storedMedia);
        } else if (storedMedia.status !== STATUS_LOADING) {
          updateStateMedia({ ...storedMedia, status: STATUS_LOADING });
          try {
            await deleteMedia(uploadURL, ignoreId ? undefined : storedMedia.data.id);
            deleteStateMedia(storedMedia, true);
          } catch (error) {
            if (error.response.status !== 404) {
              updateStateMedia({ ...storedMedia, status: STATUS_LOADED });
            }
          }
        }
      },
      [deleteStateMedia, ignoreId, updateStateMedia, uploadURL, delegateToForm]
    );

    const renderContent = React.useCallback(() => {
      if (React.isValidElement(render)) {
        return React.cloneElement(render, {
          medias: getAllMedias(currentMedias.current ?? [], queue),
          ...(!readOnly ? { onUpdate, onDelete, onRetry, onBeforeUpload } : {}),
        });
      }
      return render(
        getAllMedias(currentMedias.current ?? [], queue),
        !readOnly && onUpdate,
        !readOnly && onDelete,
        !readOnly && onRetry,
        !readOnly && onBeforeUpload
      );
    }, [queue, readOnly, render, onBeforeUpload, onRetry, onDelete, onUpdate]);

    React.useImperativeHandle(reference, () => ({
      clearQueue: () => setQueue([]),
    }));

    return (
      <>
        {!onlyRender && (
          <>
            {showUploadButton && (
              <Upload disabled={isDisabled} allowed={allowed} beforeUpload={onBeforeUpload} {...draggerProps}>
                <Button disabled={isDisabled} {...uploadButtonProps}>
                  <span className="fb-icon--upload" />
                  {!!uploadLabel && <FormattedMessage id={uploadLabel} defaultMessage="Upload media(s)" />}
                </Button>
              </Upload>
            )}

            {showUploadDragger && (
              <Dragger
                multiple
                allowed={allowed}
                disabled={isDisabled}
                className={styles.dragger}
                beforeUpload={onBeforeUpload}
                displayMaxSizeMessage={displayMaxSizeMessage}
                {...draggerProps}
              />
            )}
          </>
        )}

        {render && renderContent()}
      </>
    );
  }
);

export default MediaUploader;
